import { useCallback } from 'react';

import type { ICustomMessage } from '@/components/chat';
import type { ReferenceViewSource } from '@/components/chat/message-list/SharedReferenceView';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { openMessageLink, toOpenableUrl } from '@/util/messageLinks';
import { isSameUserId } from '@/utils/isSameUserId';

/**
 * True when a message's links count as references: the inquiry recipient
 * wrote it, or it is expert content relayed through Primum Support. Links the
 * submitter or a system message sends are not a reference someone consulted.
 */
export function isRecipientContent(
  message: Pick<ICustomMessage, 'user' | 'contentRole' | 'type'> | undefined,
  recipientId: string | number | undefined
): boolean {
  if (!message) {
    return false;
  }

  return (
    isSameUserId(message.user?._id, recipientId) ||
    message.contentRole === 'expert_relay' ||
    message.type === 'expert_relay'
  );
}

/**
 * The link as reported to analytics, or `undefined` when it is not a web page.
 *
 * Scheme, host and path only: a link typed into a message can carry a token or
 * a patient search in its query, and the path is enough to tell one article
 * from another. The host drops `www.` to match what attached references
 * report. A `mailto:` or `tel:` link is contact detail rather than a
 * reference, and has no origin to report anyway.
 */
function describeWebLink(url: string): { reference_url: string; url_host: string } | undefined {
  try {
    const parsed = new URL(toOpenableUrl(url));

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined;
    }

    return {
      reference_url: `${parsed.origin}${parsed.pathname}`,
      url_host: parsed.hostname.replace(/^www\./, ''),
    };
  } catch {
    return undefined;
  }
}

/**
 * Returns the press handler for a link inside a message's text.
 *
 * A link the recipient typed is a reference in all but name, so it reports
 * `Reference viewed` like an attached one does. `reference_source` is
 * `message_link` to keep it apart from references attached through the
 * reference tools, which report `statement_attached` or `suggested`.
 *
 * `conversation_id` is passed explicitly because the desktop web inbox renders
 * the chat outside the analytics provider that would otherwise inject it.
 */
export function useMessageLinkOpener({
  conversationId,
  messageSid,
  isReference,
}: {
  conversationId: string | undefined;
  messageSid: string | undefined;
  isReference: boolean;
}) {
  const { track } = useChatAnalytics();

  return useCallback(
    (url: string) => {
      const webLink = isReference ? describeWebLink(url) : undefined;

      if (webLink) {
        track(ANALYTICS_EVENTS.REFERENCE_VIEWED, {
          conversation_id: conversationId,
          message_sid: messageSid,
          ...webLink,
          reference_source: 'message_link' satisfies ReferenceViewSource,
        });
      }

      openMessageLink(url);
    },
    [conversationId, isReference, messageSid, track]
  );
}
